import { withApollo } from '@apollo/client/react/hoc'
import { injectIntl } from 'react-intl'
import React, { Component } from 'react'
import { Alert, Menu, Tooltip } from 'antd'
import { Query } from '@apollo/client/react/components'
import { faFolder, faListAlt } from '@fortawesome/free-regular-svg-icons'
import {
	faLink,
	faMusic,
	faAngleRight
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as Sentry from '@sentry/browser'

import Loading from '../LoadingPage'
import * as Utils from '../Utils'
import _ from 'lodash'
import gql from 'graphql-tag'
import S from '../Styles'

const styles = S.columnBrowser

/**
 * This component will display a list of playlist for a given parent playlist id
 * It will call the onSelectedChange function from prop with the selected playlist id alongside
 * a boolean indicating if the selected item is a playlist or a pool with a current playlist
 */
class PlaylistBrowserList extends Component {
	state = {
		// the selected playlist id, can be null
		selected: this.props.selected,
		parentPlaylist: null,
		currentPlaylist: null,
		firstPlaylistRendering: true
	}

	async componentDidMount() {
		const { client, parentId } = this.props
		if (parentId !== 'root') {
			const parentPlaylist = await this.fetchPlaylistInfo(parentId)
			this.setState({ parentPlaylist })

			if (parentPlaylist?.currentPlaylist) {
				const currentPlaylist = await this.fetchPlaylistInfo(
					parentPlaylist?.currentPlaylist
				)

				if (!currentPlaylist) {
					const errorMessage = `The current playlist selected for the pool ${parentId} does not exist`
					console.error(errorMessage)
					Sentry.captureException(new Error(errorMessage))
				} else {
					this.setState({ currentPlaylist })

					// if the current playlist is the one selected
					// then the selected state must be null to select the pseudo item
					if (currentPlaylist._id === this.state.selected) {
						this.setState({ selected: null })
					}
				}
			}
		}
	}

	// This lines below handle auto scroll when opening the list with a selected item
	selectedItemRef = React.createRef()
	firstUpdate = true

	scrollToSelected = () => {
		if (this.props.selected) {
			this.selectedItemRef.current?.scrollIntoView({
				block: 'center'
			})
		}
	}

	getNodeIcon = (playlist) => {
		return [faFolder, faListAlt, faMusic, faLink][playlist.y || 0]
	}

	isLeaf = (playlist) => {
		// a leaf is a playlist (y=2) or a currentPlaylist (y=3)
		// the y === 3 is not in the db, it only represent the "current playlist" item
		// in the webapp
		return playlist.y === 2 || playlist.y === 3
	}

	isSelected = (playlist) => {
		if (this.state.selected === playlist._id) {
			return true
		}

		// if we have a pseudo item for the current playlist we select it
		if (
			!this.state.selected &&
			this.state.parentPlaylist?.currentPlaylist &&
			playlist.y === 3
		) {
			return true
		}

		return false
	}

	selectItem = (item) => {
		// alongside the selected IPlaylist id we add a boolean
		// to indicate if its a valid selection aka
		// - a playlist (y=2 or y=3)
		// - a pool with a currentPlaylist (y=1 and currentPlaylist != null)

		const isValid =
			this.isLeaf(item) || (item.y === 1 && item.currentPlaylist)
		this.props.onSelectedChange({
			selectedId: item._id,
			isValid
		})
		this.setState({ selected: item._id })
	}

	/**
	 * This function take the raw playlists from the query
	 * compute a name for the playlist if missing
	 * sort the playlists by name or creation date
	 * and add the "Current playlist" item if needed
	 */
	getProcessedPlaylists = (rawPlaylists) => {
		const processList = rawPlaylists
			.map((rpl) => {
				const name =
					rpl.n ??
					this.props.intl.formatDate(rpl.created, {
						day: 'numeric',
						month: 'long',
						year: 'numeric'
					})

				return {
					...rpl,
					n: name
				}
			})
			.sort((a, b) => {
				let aa = a.n || `${a.created}`
				let bb = b.n || `${b.created}`
				return aa.localeCompare(bb)
			})

		/**
		 * If the parent node is a pool and this pool has a currentPlaylist
		 * we need to add a specific item at the top of the list
		 * to represent the current playlist
		 * this item is not in the database so we create a fake one
		 */
		const { parentPlaylist, currentPlaylist } = this.state
		if (
			this.props.addCurrentPlayListOption &&
			parentPlaylist?.y === 1 &&
			parentPlaylist?.currentPlaylist &&
			currentPlaylist
		) {
			processList.unshift({
				// the pseudo item has no id, this we be handled by the parent component
				_id: null,
				a: parentPlaylist._id,
				n: `Current playlist (${currentPlaylist.n})`,
				// the specific y=3 value is needed to have a specific icon
				y: 3
			})
		}

		return processList
	}

	fetchPlaylistInfo = async (playlistId) => {
		const { client } = this.props
		const result = await client.query({
			variables: { playlistId },
			query: gql`
				query fetchPlaylistById($playlistId: String!) {
					playlistById(_id: $playlistId) {
						_id
						n
						y
						currentPlaylist
					}
				}
			`
		})

		return result.data.playlistById
	}

	playListNameIsTooLong = (name) => {
		return name.length > 45
	}

	trimPlaylistName = (name) => {
		return this.playListNameIsTooLong(name) ?
				`${name.substring(0, 42)}...`
			:	name
	}

	render() {
		const { parentId, intl } = this.props
		return (
			<Query
				onCompleted={() => {
					// we scroll only after the first rendering with data
					if (this.state.firstPlaylistRendering) {
						this.setState({ firstPlaylistRendering: false }, () => {
							this.scrollToSelected()
						})
					}
				}}
				variables={{
					filter: { a: parentId === 'root' ? null : parentId }
				}}
				query={gql`
					query playlists($filter: FilterFindManyPlaylistInput) {
						playlists(filter: $filter) {
							_id
							n
							a
							y
							created
							currentPlaylist
						}
					}
				`}
			>
				{({ loading, error, data }) => {
					if (loading) {
						// This empty menu with a minwidth is to ensure the browser
						// scroll at least of 200px when opening a new column
						// event is the rendering is not done before the timeout in the parent cmpt
						return (
							<Menu style={{ ...styles.column, minWidth: 200 }} />
						)
					}
					if (error) {
						return (
							<Alert
								message={intl.formatMessage({
									id: 'error'
								})}
								description={Utils.displayGraphQLErrors(error)}
								type="error"
								showIcon
							/>
						)
					}

					if (!_.isEmpty(data.playlists)) {
						return (
							<Menu
								style={styles.column}
								mode="vertical"
								key={`menu-${parentId}`}
								selectedKeys={[
									`playlist-${this.state.selected}`
								]}
							>
								{this.getProcessedPlaylists(data.playlists).map(
									(playlist) => (
										<Menu.Item
											onClick={() =>
												this.selectItem(playlist)
											}
											key={`playlist-${playlist._id}`}
											className={
												this.isSelected(playlist) ?
													'ant-menu-item-selected'
												:	''
											}
										>
											<div
												style={styles.row}
												ref={
													this.isSelected(playlist) ?
														this.selectedItemRef
													:	null
												}
											>
												<Tooltip
													title={
														(
															this.playListNameIsTooLong(
																playlist.n
															)
														) ?
															playlist.n
														:	null
													}
												>
													<span>
														<FontAwesomeIcon
															icon={this.getNodeIcon(
																playlist
															)}
														/>
														&nbsp;&nbsp;
														{this.trimPlaylistName(
															playlist.n
														)}
													</span>
													{!this.isLeaf(playlist) && (
														<FontAwesomeIcon
															style={
																styles.expandIcon
															}
															icon={faAngleRight}
														/>
													)}
												</Tooltip>
											</div>
										</Menu.Item>
									)
								)}
							</Menu>
						)
					} else {
						return <></>
					}
				}}
			</Query>
		)
	}
}
export default withApollo(
	injectIntl(
		React.forwardRef((props, ref) => (
			<PlaylistBrowserList {...props} ref={ref} />
		))
	)
)
